import { BUDGET, WIDGET_TYPE } from 'business/constants';
import React, { useState } from 'react';
import EditModal from 'routes/BudgetModule/components/BudgetProposal/Resource/Arguments/Argument/Widgets/EditModal';
import EditNavLinkModal from 'routes/BudgetModule/components/BudgetProposal/Resource/Arguments/Argument/Widgets/EditNavLinkModal';
import { useTranslation } from 'react-i18next';
import {
    WidgetContent,
    WidgetT,
} from 'routes/Dashboard/components/WidgetsLayout/helpers';
import ConfirmDeleteModal from 'components/ConfirmDeleteModal';
import DashboardChartWidget, {
    DashboardChartWidgetProps,
} from 'routes/Dashboard/components/WidgetsLayout/Widgets/Chart';
import ArgumentChartWidget, {
    ArgumentChartWidgetProps,
} from 'routes/BudgetModule/components/BudgetProposal/Resource/Arguments/Argument/Widgets/ChartWidget';
import PresentationModeContext from 'routes/Dashboard/components/PresentationModeContext';
import NavLinkWidget from '../NavLinkWidget/NavLinkWidget';
import ParagraphWidget from '../ParagraphWidget';
import SeparatorWidget from '../SeparatorWidget';
import TitleWidget from '../TitleWidget';
import PageBreakWidget from '../PageBreakWidget';
import TableOfContentWidget from '../TableOfContentWidget';
import WidgetContext from '../helpers/widgetContext';
import { useNavigate, useParams } from 'react-router-dom';
import {
    getDashboardParamForExploreRoute,
    getWidgetPositionParamForExploreRoute,
} from 'routes/Explore/helpers';
import { useWidgetPositionContext } from 'lib/layout/context/WidgetPositionContext';
import { useIsExploreUser } from 'hooks/client';
import { addInsertItem } from '../helpers';
import FullScreenProvider from 'contexts/FullScreenContextProvider';
import useAddWidget from 'hooks/useAddWidget';
import { getPublicToken } from 'components/QueryPerimeter/hooks/useGetTreeData';
import { useDispatch } from 'store/hooks';
import { WidgetParent } from 'components/Widget/WidgetFromType/constants';
import { CHART_ACTION_TYPE } from 'ui/NestedMenu/constants';

interface BaseProps {
    widget: WidgetT;
    onUpdate: (arg1: { widget: WidgetT; content: WidgetContent }) => void;
    editModeState: [boolean, (arg: boolean) => void];
    canEdit: boolean;
    isPdfModeActivated: boolean;
    onDelete: () => Promise<void>;
}

interface DashboardWidgetProps extends BaseProps {
    widgetParent: WidgetParent.DASHBOARD;
    chartProps: Omit<
        DashboardChartWidgetProps,
        'chart' | 'editMode' | 'canEdit' | 'isPdfModeActivated'
    >;
}
interface ArgumentWidgetProps extends BaseProps {
    widgetParent: WidgetParent.ARGUMENT;
    chartProps: Pick<ArgumentChartWidgetProps, 'lazyLoading'>;
}

type Props = DashboardWidgetProps | ArgumentWidgetProps;

const WidgetFromType = ({
    widgetParent,
    widget,
    chartProps,
    onUpdate,
    editModeState,
    canEdit,
    isPdfModeActivated,
    onDelete,
}: Props): React.ReactElement => {
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editMode, setEditMode] = editModeState;
    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
    const [isInPresentationMode] = React.useContext(PresentationModeContext);
    const navigate = useNavigate();
    const widgetPosition = useWidgetPositionContext();
    const { slug } = useParams<{ slug: string }>();
    const isExploreUser = useIsExploreUser();
    const dispatch = useDispatch();

    const getDeleteConfirm = (title: string) => (
        <ConfirmDeleteModal
            show={showConfirmDeleteModal}
            cancel={() => {
                setShowConfirmDeleteModal(false);
            }}
            confirm={() => {
                setShowConfirmDeleteModal(false);
                onDelete();
            }}
            title={title}
        />
    );

    const { t } = useTranslation();

    const addWidgetMutation = useAddWidget(
        slug,
        getPublicToken(),
        (err) =>
            dispatch.vision.notifyUser({
                message: err.message,
                snackType: 'error',
            }),
        widgetParent === WidgetParent.ARGUMENT ? 'argument' : 'dashboard',
    );

    const updateItem = {
        action: CHART_ACTION_TYPE.Update,
        menuLevel: 0,
        onClick: () => setIsEditModalOpen(true),
    };
    const resizeItem = {
        action: CHART_ACTION_TYPE.Resize,
        menuLevel: 0,
        onClick: () => setEditMode(true),
    };
    const deleteItem = {
        action: CHART_ACTION_TYPE.Delete,
        menuLevel: 0,
        onClick: () => {
            setShowConfirmDeleteModal(true);
        },
    };
    const insertChartItem = {
        action: CHART_ACTION_TYPE.Insert,
        menuLevel: 0,
        onClick: () => {},
        nestedOptions: [
            ...(widgetParent === WidgetParent.DASHBOARD
                ? [
                      {
                          action: CHART_ACTION_TYPE.InsertIndicator,
                          onClick: () =>
                              navigate(
                                  `/explore${getDashboardParamForExploreRoute(slug)}&${getWidgetPositionParamForExploreRoute(widgetPosition)}`,
                              ),
                          menuLevel: 1,
                      },
                  ]
                : []),
            {
                action: CHART_ACTION_TYPE.InsertTitle,
                onClick: () =>
                    addWidgetMutation.mutate({
                        type: 'title',
                        layoutId: widget.layoutId,
                        position: widgetPosition,
                    }),
                menuLevel: 1,
            },
            {
                action: CHART_ACTION_TYPE.ViewHeadline,
                onClick: () =>
                    addWidgetMutation.mutate({
                        type: 'paragraph',
                        layoutId: widget.layoutId,
                        position: widgetPosition,
                    }),
                menuLevel: 1,
            },
            {
                action: CHART_ACTION_TYPE.Separator,
                onClick: () =>
                    addWidgetMutation.mutate({
                        type: 'separator',
                        layoutId: widget.layoutId,
                        position: widgetPosition,
                    }),
                menuLevel: 1,
            },
            {
                action: CHART_ACTION_TYPE.AddLink,
                onClick: () =>
                    addWidgetMutation.mutate({
                        type: 'navlink',
                        layoutId: widget.layoutId,
                        position: widgetPosition,
                    }),
                menuLevel: 1,
            },
            {
                action: CHART_ACTION_TYPE.InsertPageBreak,
                onClick: () =>
                    addWidgetMutation.mutate({
                        type: 'pagebreak',
                        layoutId: widget.layoutId,
                        position: widgetPosition,
                    }),
                menuLevel: 1,
            },
            ...(widgetParent === WidgetParent.DASHBOARD
                ? [
                      {
                          action: CHART_ACTION_TYPE.FormatBulletedList,
                          onClick: () =>
                              addWidgetMutation.mutate({
                                  type: 'tableofcontent',
                                  layoutId: widget.layoutId,
                                  position: widgetPosition,
                              }),
                          menuLevel: 1,
                      },
                  ]
                : []),
        ],
    };

    const chartWidget = () => {
        switch (widgetParent) {
            case WidgetParent.DASHBOARD:
                return (
                    <WidgetContext.Provider value={widget}>
                        <DashboardChartWidget
                            // TODO StrictNullCheck : Try to remove "!"
                            chart={widget.chart!}
                            editMode={editMode}
                            canEdit={canEdit}
                            isPdfModeActivated={isPdfModeActivated}
                            {...chartProps}
                        />
                    </WidgetContext.Provider>
                );
            case WidgetParent.ARGUMENT:
                return (
                    // If we don't pass an height, the widget will have a height of 0. We disable
                    // escape because we rely on the click event to be able to restore the initial
                    // CSS of the widget.
                    <FullScreenProvider
                        height="inherit"
                        disableEscape
                        module={BUDGET}
                    >
                        <ArgumentChartWidget
                            widget={widget}
                            menuItems={[
                                updateItem,
                                resizeItem,
                                deleteItem,
                                insertChartItem,
                            ]}
                            editMode={editMode}
                            isPdfModeActivated={isPdfModeActivated}
                            {...chartProps}
                        />
                    </FullScreenProvider>
                );
            default:
                throw new Error();
        }
    };

    switch (widget.type) {
        case WIDGET_TYPE.chart:
            if (!widget.chart) {
                throw new Error(
                    'Argumentaire widget renderer: chart should be defined',
                );
            }

            return (
                <>
                    {chartWidget()}
                    <EditModal
                        widget={widget}
                        isEditModalOpen={isEditModalOpen}
                        setIsEditModalOpen={setIsEditModalOpen}
                        value={widget.chart.title}
                        onEditWidget={onUpdate}
                    />
                    {getDeleteConfirm(t('budgetProposal.chartDeleteConfirm'))}
                </>
            );
        case WIDGET_TYPE.separator:
            return (
                <>
                    <SeparatorWidget
                        menuItems={addInsertItem(
                            [resizeItem, deleteItem],
                            insertChartItem,
                            canEdit,
                            isExploreUser,
                        )}
                        editMode={editMode}
                        canEdit={canEdit}
                    />
                    {getDeleteConfirm(
                        t('budgetProposal.separatorDeleteConfirm'),
                    )}
                </>
            );
        case WIDGET_TYPE.pagebreak:
            return (
                <>
                    <PageBreakWidget
                        menuItems={addInsertItem(
                            [deleteItem],
                            insertChartItem,
                            canEdit,
                            isExploreUser,
                        )}
                        editMode={editMode}
                        canEdit={canEdit}
                        isPdfModeActivated={isPdfModeActivated}
                    />
                    {getDeleteConfirm(
                        t('budgetProposal.pageBreakDeleteConfirm'),
                    )}
                </>
            );
        case WIDGET_TYPE.tableofcontent:
            if (widgetParent !== WidgetParent.DASHBOARD) {
                throw new Error(
                    `Argumentaire widget renderer: content should be defined for ${widget.type}`,
                );
            }
            return (
                <>
                    <TableOfContentWidget
                        editMode={editMode}
                        canEdit={canEdit}
                        menuItems={addInsertItem(
                            [resizeItem, deleteItem],
                            insertChartItem,
                            canEdit,
                            isExploreUser,
                        )}
                        isPdfModeActivated={isPdfModeActivated}
                    />
                    {getDeleteConfirm(
                        t('budgetProposal.tableOfContentDeleteConfirm'),
                    )}
                </>
            );
        case WIDGET_TYPE.navlink:
            if (!widget.content) {
                throw new Error(
                    `Argumentaire widget renderer: content should be defined for ${widget.type}`,
                );
            }

            return (
                <>
                    <NavLinkWidget
                        widget={widget}
                        menuItems={addInsertItem(
                            [updateItem, resizeItem, deleteItem],
                            insertChartItem,
                            canEdit,
                            isExploreUser,
                        )}
                        editMode={editMode}
                        canEdit={canEdit}
                    />

                    {isEditModalOpen && (
                        <EditNavLinkModal
                            widget={widget}
                            isEditModalOpen={isEditModalOpen}
                            setIsEditModalOpen={setIsEditModalOpen}
                            onEditWidget={onUpdate}
                        />
                    )}
                    {getDeleteConfirm(t('widget.navlink.deleteConfirm'))}
                </>
            );
        case WIDGET_TYPE.title:
            if (!widget.content) {
                throw new Error(
                    `Argumentaire widget renderer: content should be defined for ${widget.type}`,
                );
            }

            return (
                <>
                    <TitleWidget
                        widget={widget}
                        menuItems={addInsertItem(
                            [updateItem, resizeItem, deleteItem],
                            insertChartItem,
                            canEdit,
                            isExploreUser,
                        )}
                        editMode={editMode}
                        canEdit={canEdit}
                        onDoubleClick={() => {
                            if (canEdit && !isInPresentationMode) {
                                setIsEditModalOpen(true);
                            }
                        }}
                    />

                    <EditModal
                        widget={widget}
                        isEditModalOpen={isEditModalOpen}
                        setIsEditModalOpen={setIsEditModalOpen}
                        value={widget.content.text || ''}
                        onEditWidget={onUpdate}
                    />
                    {getDeleteConfirm(t('budgetProposal.titleDeleteConfirm'))}
                </>
            );
        case WIDGET_TYPE.paragraph:
            if (!widget.content) {
                throw new Error(
                    `Argumentaire widget renderer: content should be defined for ${widget.type}`,
                );
            }

            return (
                <>
                    <ParagraphWidget
                        widget={widget}
                        widgetParent={widgetParent}
                        menuItems={addInsertItem(
                            [resizeItem, deleteItem],
                            insertChartItem,
                            canEdit,
                            isExploreUser,
                        )}
                        editMode={editMode}
                        canEdit={canEdit}
                        onEditWidget={onUpdate}
                    />
                    {getDeleteConfirm(
                        t('budgetProposal.paragraphDeleteConfirm'),
                    )}
                </>
            );
        default:
            throw new Error(`Unknown widget type: ${widget.type}`);
    }
};

export default React.memo(WidgetFromType);
